/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';

const LOW_RES_SCALE_FACTOR = 0.1;

const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const LOAD_ENUM = Object.freeze({
  TRANSPARENT: 1,
  LOADING_LOW: 2,
  LOADING_HIGH: 3,
  DONE: 4,
});

/**
 * Dynamic img component to :
 * - lazy load images using the brozser "loading" property
 * - display lower resolution img while the better img is loading
 * - use the devicePixelRatio
 * @param {*} param0
 */
function Image({
  imageUrl,
  width,
  format,
  sharpen,
  backgroundColor,
  transparentBackground,
  alt,
  onLowDefLoaded,
  ...rest
}) {
  const [loadingState, setLoadingState] = useState(LOAD_ENUM.TRANSPARENT);

  if (!imageUrl) return null;

  // Iterate over all loading state to select the next one
  function nextLoadingState() {
    if (loadingState === LOAD_ENUM.TRANSPARENT) {
      setLoadingState(LOAD_ENUM.LOADING_LOW);
    } else if (loadingState === LOAD_ENUM.LOADING_LOW) {
      onLowDefLoaded();
      setLoadingState(LOAD_ENUM.LOADING_HIGH);
    } else if (loadingState === LOAD_ENUM.LOADING_HIGH) {
      setLoadingState(LOAD_ENUM.DONE);
    }
  }

  const pixelRatio = window.devicePixelRatio;
  const ratio = pixelRatio > 1 ? 2 : 1;

  const lowResWidth = width * ratio * LOW_RES_SCALE_FACTOR;
  const highResWidth = width * ratio;
  const url = new URL(imageUrl);
  const baseUrl = url.origin + url.pathname;

  let srcUrl = TRANSPARENT_PIXEL;

  if (loadingState !== LOAD_ENUM.TRANSPARENT) {
    let query = stringifyQuery({
      ...parseQuery(url.search),
      width:
        loadingState === LOAD_ENUM.LOADING_LOW ? lowResWidth : highResWidth,
      mode: 'fill',
      format,
      sharpen,
      ...(!transparentBackground && { backgroundColor }),
    });

    srcUrl = `${baseUrl}?${query}`;
  }

  return (
    <StyledImg
      src={srcUrl}
      onLoad={nextLoadingState}
      loading="lazy"
      alt={alt}
      {...(loadingState !== LOAD_ENUM.DONE && { className: 'blurred' })}
      {...rest}
    />
  );
}

const StyledImg = styled.img`
  transition: 0.5s filter linear;
  &.blurred {
    filter: blur(3px);
  }
`;

Image.defaultProps = {
  width: 200,
  format: 'jpg',
  sharpen: false,
  backgroundColor: '0,0,0',
  transparentBackground: false,
  onLowDefLoaded: () => {},
};

export default Image;
